import React, { useMemo, useState } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { srLatn } from "date-fns/locale/sr-Latn";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-theme.css";
import { IconCalendar } from "./icons";
import { CONTROL, LABEL } from "./controlStyles";
import { fromDateInputValue, toDateInputValue } from "../../utils/date";

/**
 * The single date picker for the whole project.
 *
 * value / onChange work with a "yyyy-MM-dd" string (the same format the backend
 * expects in query parameters), so the state on the pages that use it
 * does not need to change - only the display does.
 */
registerLocale("sr-Latn", srLatn);

// Display-only DOM field: it cannot be typed into, but a click still opens the calendar.
const ReadOnlyInput = React.forwardRef(function ReadOnlyInput(props, ref) {
  return <input ref={ref} {...props} readOnly />;
});

export default function DatePicker({ label, value, onChange, id, placeholder = "Izaberi datum" }) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");
  const selectedDate = useMemo(() => fromDateInputValue(value), [value]);
  const [open, setOpen] = useState(false);

  const control = (
    <div className="relative">
      <ReactDatePicker
        id={inputId}
        selected={selectedDate}
        open={open}
        onInputClick={() => setOpen(true)}
        onClickOutside={() => setOpen(false)}
        onChange={(date) => {
          onChange(date ? toDateInputValue(date) : "");
          // setTimeout instead of a synchronous close gives the library a chance
          // to finish its internal reaction to the changed "selected" before
          // WE explicitly close it, avoiding a race that has so far
          // reopened the calendar after a synchronous/useEffect close.
          setTimeout(() => setOpen(false), 0);
        }}
        locale="sr-Latn"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        yearDropdownItemNumber={12}
        dateFormat="dd.MM.yyyy"
        placeholderText={placeholder}
        autoComplete="off"
        // No typing: the field only opens the calendar (no keyboard pops up on a phone).
        // readOnly must NOT go on the library prop (it blocks day selection), so it is only on the DOM field.
        customInput={<ReadOnlyInput />}
        inputMode="none"
        portalId="datepicker-portal"
        popperPlacement="bottom-start"
        className={`${CONTROL} w-36 cursor-pointer pr-9 font-medium tabular-nums`}
      />
      <IconCalendar
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 dark:text-slate-400"
      />
    </div>
  );

  if (!label) return control;

  return (
    <label htmlFor={inputId} className="flex flex-col gap-1.5">
      <span className={LABEL}>{label}</span>
      {control}
    </label>
  );
}
